import { createGenericStore } from '@/stores/utils/genericStore.js' // Assumes genericStore.js is in your main app's 'resources/js/utils/'
import axios from 'axios'

// 1. Define your custom options
const TaskTemplateStoreOptions = {
  state: () => ({
    launchingTaskTemplateIds: [],
    updatingScheduleTaskTemplateIds: [],
    updatingScheduleStatusTaskTemplateIds: [],
    updatingDefaultDeadlineTaskTemplateIds: [],
    updatingPriorityTaskTemplateIds: [],
    updatingAssigneeTaskTemplateIds: [],
    updatingTitleTaskTemplateIds: [],
    updatingDescriptionTaskTemplateIds: [],
  }),

  // Custom getters
  getters: {
    isLaunching(state) {
      return (taskTemplateId) => state.launchingTaskTemplateIds.includes(taskTemplateId)
    },
    isUpdatingDefaultDeadline(state) {
      return (taskTemplateId) => state.updatingDefaultDeadlineTaskTemplateIds.includes(taskTemplateId)
    },
    isUpdatingSchedule(state) {
      return (taskTemplateId) => state.updatingScheduleTaskTemplateIds.includes(taskTemplateId)
    },
    isUpdatingScheduleStatus(state) {
      return (taskTemplateId) => state.updatingScheduleStatusTaskTemplateIds.includes(taskTemplateId)
    },
    isUpdatingPriority(state) {
      return (taskTemplateId) => state.updatingPriorityTaskTemplateIds.includes(taskTemplateId)
    },
    isUpdatingAssignee(state) {
      return (taskTemplateId) => state.updatingAssigneeTaskTemplateIds.includes(taskTemplateId)
    },
    isUpdatingTitle(state) {
      return (taskTemplateId) => state.updatingTitleTaskTemplateIds.includes(taskTemplateId)
    },
    isUpdatingDescription(state) {
      return (taskTemplateId) => state.updatingDescriptionTaskTemplateIds.includes(taskTemplateId)
    },
  },

  // Custom actions
  actions: {
    async launchTemplate(taskTemplateId, payload = {}) {
      this.error = null
      this.launchingTaskTemplateIds.push(taskTemplateId)

      try {
        const { data } = await axios.post(`${this.api}/${taskTemplateId}/launch`, payload)
        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.launchingTaskTemplateIds = this.launchingTaskTemplateIds.filter((id) => id !== taskTemplateId)
      }
    },
    async updateDefaultDeadline(taskTemplateId, defaultDeadlineAfterMinutes) {
      this.error = null
      this.updatingDefaultDeadlineTaskTemplateIds.push(taskTemplateId)

      try {
        const { data } = await axios.patch(`${this.api}/${taskTemplateId}/default-deadline`, {
          default_deadline_after_minutes: defaultDeadlineAfterMinutes,
        })
        this.itemPatchState(data)
        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingDefaultDeadlineTaskTemplateIds = this.updatingDefaultDeadlineTaskTemplateIds.filter((id) => id !== taskTemplateId)
      }
    },
    async updateSchedule(taskTemplateId, schedulePayload) {
      this.error = null
      this.updatingScheduleTaskTemplateIds.push(taskTemplateId)

      try {
        const { data } = await axios.patch(`${this.api}/${taskTemplateId}`, {
          schedule: schedulePayload,
        })
        this.itemPatchState(data)
        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingScheduleTaskTemplateIds = this.updatingScheduleTaskTemplateIds.filter((id) => id !== taskTemplateId)
      }
    },
    async clearSchedule(taskTemplateId) {
      return this.updateSchedule(taskTemplateId, null)
    },
    async updateScheduleStatus(taskTemplateId, isActive) {
      this.error = null
      this.updatingScheduleStatusTaskTemplateIds.push(taskTemplateId)

      try {
        const { data } = await axios.patch(`${this.api}/${taskTemplateId}/schedule-status`, {
          is_active: isActive,
        })
        this.itemPatchState(data)
        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingScheduleStatusTaskTemplateIds = this.updatingScheduleStatusTaskTemplateIds.filter((id) => id !== taskTemplateId)
      }
    },
    async updatePriority(taskTemplateId, priority) {
      this.error = null
      this.updatingPriorityTaskTemplateIds.push(taskTemplateId)

      try {
        const { data } = await axios.patch(`${this.api}/${taskTemplateId}`, {
          priority,
        })
        this.itemPatchState(data)
        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingPriorityTaskTemplateIds = this.updatingPriorityTaskTemplateIds.filter((id) => id !== taskTemplateId)
      }
    },
    async updateAssignee(taskTemplateId, assignedToUserId) {
      this.error = null
      this.updatingAssigneeTaskTemplateIds.push(taskTemplateId)

      try {
        const { data } = await axios.patch(`${this.api}/${taskTemplateId}`, {
          assigned_to_user_id: assignedToUserId,
        })
        this.itemPatchState(data)
        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingAssigneeTaskTemplateIds = this.updatingAssigneeTaskTemplateIds.filter((id) => id !== taskTemplateId)
      }
    },
    async updateTitle(taskTemplateId, title) {
      this.error = null
      this.updatingTitleTaskTemplateIds.push(taskTemplateId)

      try {
        const { data } = await axios.patch(`${this.api}/${taskTemplateId}`, {
          title,
        })
        this.itemPatchState(data)
        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingTitleTaskTemplateIds = this.updatingTitleTaskTemplateIds.filter((id) => id !== taskTemplateId)
      }
    },
    async updateDescription(taskTemplateId, description) {
      this.error = null
      this.updatingDescriptionTaskTemplateIds.push(taskTemplateId)

      try {
        const { data } = await axios.patch(`${this.api}/${taskTemplateId}`, {
          description,
        })
        this.itemPatchState(data)
        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingDescriptionTaskTemplateIds = this.updatingDescriptionTaskTemplateIds.filter((id) => id !== taskTemplateId)
      }
    },
  }
}

// 2. Create the store hook using your generic factory
export const useTaskTemplateStore = createGenericStore(
  'TaskTemplate', // Base Store ID
  '/api/v1/task-templates', // API Endpoint
  TaskTemplateStoreOptions // Pass in your custom logic
)
